/**
 * Canonical phase keys emitted by the backend on `phases` companion arrays
 * and on `external_section_map.labels`. The seven values are the contract
 * between BE and FE: chart math, stacking, and click filters all key on
 * these strings; only the visible label is per-workflow overridable.
 */
enum PhaseKey {
  Backlog = 'backlog',
  Blocked = 'blocked',
  Wip = 'wip',
  Review = 'review',
  Test = 'test',
  Deployable = 'deployable',
  Done = 'done',
}

/**
 * Canonical iteration order. Matches the backend's `CANONICAL_BUCKET_KEYS`
 * tuple — preserve this order so chart legends, tile rows, and tooltip
 * itemSorters stay stable across responses with or without overrides.
 */
const CANONICAL_PHASE_KEYS: readonly PhaseKey[] = [
  PhaseKey.Backlog,
  PhaseKey.Blocked,
  PhaseKey.Wip,
  PhaseKey.Review,
  PhaseKey.Test,
  PhaseKey.Deployable,
  PhaseKey.Done,
] as const;

/**
 * Maps the historic Bloomfilter workflow step names — the values stored in
 * `Step.name`, which the Workflow Map UI renders through the per-workflow
 * display labels rather than verbatim — onto canonical phase keys.
 *
 * Lives here beside `CANONICAL_PHASE_KEYS` so step-name reconciliation has one
 * home: the step name "Waiting" corresponds to canonical key `blocked`, and
 * "In Progress"/"In Review"/"In Test" to `wip`/`review`/`test`.
 *
 * Keys are the exact stored casing. Consumers that may see differently cased or
 * padded step names should normalize before looking up, the way
 * `getOrderedStatusesFromWorkflows` does — an exact-match miss here reads as
 * "not a canonical step", which is silent and easy to misdiagnose.
 */
const STEP_NAME_TO_PHASE_KEY: Record<string, PhaseKey> = {
  Backlog: PhaseKey.Backlog,
  Waiting: PhaseKey.Blocked,
  'In Progress': PhaseKey.Wip,
  'In Review': PhaseKey.Review,
  'In Test': PhaseKey.Test,
  Deployable: PhaseKey.Deployable,
  Done: PhaseKey.Done,
};

/**
 * Single entry of the `phases: [...]` companion array a backend response
 * carries on phase-aware measure payloads (sprint burns/flows, work-period
 * `flow_by_phase` / `burns_and_flows` / `bloomcast_by_phase` / `task_flow`).
 */
type PhaseLabel = {
  key: PhaseKey | string;
  label: string;
};

/**
 * The fully-resolved label map produced by `resolvePhaseLabels`. Always
 * includes every canonical key so consumers can index without optional
 * chaining or fallback branches.
 */
type ResolvedPhaseLabels = Record<PhaseKey, string>;

/**
 * Fully-qualified i18n key (under `common.workflow_steps.*`) for each
 * canonical phase key. Centralized here so the resolver is the only site
 * that has to reconcile the historical `wip↔in_progress` /
 * `blocked↔waiting` / `review↔in_review` / `test↔in_test` mismatch between
 * backend canonical keys and the dictionary's human-readable identifiers.
 *
 * Stored as fully-qualified literals so consumers can pass them directly
 * to i18next's strict-typed `t(...)` without template-string concatenation
 * (which would defeat literal-key inference in TypeScript strict mode).
 */
const PHASE_KEY_TO_I18N_KEY = {
  [PhaseKey.Backlog]: 'common.workflow_steps.backlog',
  [PhaseKey.Blocked]: 'common.workflow_steps.waiting',
  [PhaseKey.Wip]: 'common.workflow_steps.in_progress',
  [PhaseKey.Review]: 'common.workflow_steps.in_review',
  [PhaseKey.Test]: 'common.workflow_steps.in_test',
  [PhaseKey.Deployable]: 'common.workflow_steps.deployable',
  [PhaseKey.Done]: 'common.workflow_steps.done',
} as const;

export { CANONICAL_PHASE_KEYS, PHASE_KEY_TO_I18N_KEY, PhaseKey, STEP_NAME_TO_PHASE_KEY };
export type { PhaseLabel, ResolvedPhaseLabels };
